"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { Phone, MapPin } from "lucide-react"
import { getTokenFromStorage, getLocalIdFromToken, getEstabelecimentoIdFromToken } from "@/lib/auth"
import OptimizedImage from "@/components/optimized-image"

export default function MenuHero() {
  const [currentImage, setCurrentImage] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [greeting, setGreeting] = useState("Olá")
  const [data, setData] = useState<any>(null)
  const [localId, setLocalId] = useState<string | null>(null)
  const [estabelecimentoId, setEstabelecimentoId] = useState<string | null>(null)

  // Obter dados do token
  useEffect(() => {
    const token = getTokenFromStorage()
    if (token) {
      const extractedLocalId = getLocalIdFromToken(token)
      const extractedEstabelecimentoId = getEstabelecimentoIdFromToken(token)

      setLocalId(extractedLocalId)
      setEstabelecimentoId(extractedEstabelecimentoId)
    }
  }, [])

  // Definir cumprimento
  useEffect(() => {
    const hour = new Date().getHours()
    if (hour < 12) setGreeting("Bom dia")
    else if (hour < 18) setGreeting("Boa tarde")
    else setGreeting("Boa noite")
  }, [])

  // Carregar do cache local ou buscar API
  useEffect(() => {
    if (!localId) return

    const cached = localStorage.getItem(`localData_${localId}`)
    if (cached) {
      setData(JSON.parse(cached))
    }

    fetch(`https://api.syncmenu.ao/generic/estabelecimento/${localId}`)
      .then((res) => res.json())
      .then((json) => {
        const content = json.localDeAtendimento
        setData(content)
        localStorage.setItem(`localData_${localId}`, JSON.stringify(content))
      })
      .catch(console.error)
  }, [localId])

  // Slide de imagens, se houver mais de uma
  useEffect(() => {
    if (!data?.secaoId?.urls || data.secaoId.urls.length <= 1) return
    const interval = setInterval(() => {
      setLoaded(false)
      setTimeout(() => {
        setCurrentImage((prev) => (prev + 1) % data.secaoId.urls.length)
      }, 400)
    }, 10000)
    return () => clearInterval(interval)
  }, [data])

  if (!data) {
    return (
      <div className="h-[55vh] sm:h-[70vh] w-full bg-background flex items-center justify-center text-foreground text-lg">
        Carregando informações...
      </div>
    )
  }

  const imagens = data.secaoId.urls
  const imageUrl = imagens[currentImage] || imagens[0]

  return (
    <div className="relative min-h-[62vh] sm:min-h-[70vh] w-full overflow-hidden rounded-b-3xl shadow-lg">
      {/* Fundo */}
      <div className="absolute inset-0">
        <OptimizedImage
          src={imageUrl || "/placeholder.svg"}
          alt="Fundo Hero"
          priority
          className={`absolute inset-0 ${loaded ? "" : ""}`}
          style={{ opacity: loaded ? 1 : 0.4, transition: "opacity 0.6s ease", filter: "brightness(0.72)" }}
          onLoad={() => setLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-black/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      {/* Conteúdo */}
      <div className="relative z-10 h-full min-h-[62vh] sm:min-h-[70vh] w-full flex flex-col justify-between px-4 sm:px-6 md:px-12 py-6 sm:py-8 text-white">
        {/* Topo: Título + Mesa */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs sm:text-sm md:text-base opacity-80">{greeting}, seja bem-vindo(a)</span>

            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.2 }}
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight"
            >
              {data.estabelecimentoId.nome}
            </motion.h1>

            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 1 }}
              className="text-[0.7rem] sm:text-sm tracking-widest text-orange-300 font-medium"
            >
              {data.secaoId.nome}
            </motion.span>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex flex-col items-end flex-shrink-0"
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 border border-white/20 shadow-md">
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-orange-300" />
                <div className="text-right leading-tight">
                  <div className="text-sm sm:text-lg font-semibold text-white max-w-[10rem] truncate">
                    {data.nome}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Descrição + Informações */}
        <div className="flex flex-col gap-4">
          <div className="text-xs sm:text-sm md:text-base space-y-1 max-w-md">
            <p className="opacity-90">
              Viva uma experiência única no coração da Huíla — onde o sabor encontra o requinte ✨
            </p>
            <p className="opacity-80">Ambiente acolhedor, pratos irresistíveis e atendimento que encanta.</p>
          </div>

          {/* Contato */}
          <div className="flex flex-wrap items-center gap-3 text-sm sm:text-base">
            <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full border border-white/20 backdrop-blur">
              <Phone className="w-4 h-4" />
              <span className="text-xs sm:text-sm">{data.estabelecimentoId.telefone || "Telefone não disponível"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}